import React, { useState, useEffect, useRef } from 'react';
import { FaClipboardList, FaSpinner, FaCalendarAlt, FaComment, FaUserFriends, FaSignature, FaSave, FaCheck } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NextStepsFeedback = ({ clientId, advisorId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    review_frequency: 'Quarterly',
    next_review_date: '',
    client_feedback: '',
    referral_name_1: '',
    referral_phone_1: '',
    referral_name_2: '',
    referral_phone_2: '',
    referral_name_3: '',
    referral_phone_3: '',
    client_signature: null,
    advisor_signature: null,
    client_signed_date: '',
    advisor_signed_date: ''
  });

  // Canvas refs for signatures
  const clientCanvasRef = useRef(null);
  const advisorCanvasRef = useRef(null);
  const [isDrawingClient, setIsDrawingClient] = useState(false);
  const [isDrawingAdvisor, setIsDrawingAdvisor] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/next-steps-feedback/${clientId}`);
      if (response.data) {
        setFormData({
          review_frequency: response.data.review_frequency || 'Quarterly',
          next_review_date: response.data.next_review_date || '',
          client_feedback: response.data.client_feedback || '',
          referral_name_1: response.data.referral_name_1 || '',
          referral_phone_1: response.data.referral_phone_1 || '',
          referral_name_2: response.data.referral_name_2 || '',
          referral_phone_2: response.data.referral_phone_2 || '',
          referral_name_3: response.data.referral_name_3 || '',
          referral_phone_3: response.data.referral_phone_3 || '',
          client_signature: response.data.client_signature || null,
          advisor_signature: response.data.advisor_signature || null,
          client_signed_date: response.data.client_signed_date || '',
          advisor_signed_date: response.data.advisor_signed_date || ''
        });
        
        // Load existing signatures to canvas
        if (response.data.client_signature) {
          loadSignatureToCanvas(clientCanvasRef, response.data.client_signature);
        }
        if (response.data.advisor_signature) {
          loadSignatureToCanvas(advisorCanvasRef, response.data.advisor_signature);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSignatureToCanvas = (canvasRef, signatureData) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = signatureData;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const saveData = async () => {
    setSaving(true);
    try {
      // Get signatures from canvas
      const clientSig = clientCanvasRef.current?.toDataURL('image/png');
      const advisorSig = advisorCanvasRef.current?.toDataURL('image/png');
      
      await axios.post(`${API}/next-steps-feedback`, {
        client_id: clientId,
        advisor_id: advisorId,
        ...formData,
        client_signature: clientSig || formData.client_signature,
        advisor_signature: advisorSig || formData.advisor_signature
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  // Signature drawing functions
  const startDrawing = (e, isClient) => {
    const canvas = isClient ? clientCanvasRef.current : advisorCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    if (isClient) {
      setIsDrawingClient(true);
    } else {
      setIsDrawingAdvisor(true);
    }
    setSaved(false);
  };

  const draw = (e, isClient) => {
    if (isClient && !isDrawingClient) return;
    if (!isClient && !isDrawingAdvisor) return;
    
    const canvas = isClient ? clientCanvasRef.current : advisorCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#FFD700';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (isClient) => {
    if (isClient) {
      setIsDrawingClient(false);
    } else {
      setIsDrawingAdvisor(false);
    }
  };

  const clearSignature = (isClient) => {
    const canvas = isClient ? clientCanvasRef.current : advisorCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (isClient) {
      setFormData(prev => ({ ...prev, client_signature: null, client_signed_date: '' }));
    } else {
      setFormData(prev => ({ ...prev, advisor_signature: null, advisor_signed_date: '' }));
    }
    setSaved(false);
  };

  const confirmSignature = (isClient) => {
    const today = new Date().toISOString().split('T')[0];
    if (isClient) {
      setFormData(prev => ({ ...prev, client_signed_date: today }));
    } else {
      setFormData(prev => ({ ...prev, advisor_signed_date: today }));
    }
  };

  // Calculate default next review date based on frequency
  const calculateNextReviewDate = (frequency) => {
    const today = new Date();
    let nextDate = new Date();
    
    switch (frequency) {
      case 'Quarterly':
        nextDate.setMonth(today.getMonth() + 3);
        break;
      case 'Half-Yearly':
        nextDate.setMonth(today.getMonth() + 6);
        break;
      case 'Yearly':
        nextDate.setFullYear(today.getFullYear() + 1);
        break;
      default:
        nextDate.setMonth(today.getMonth() + 3);
    }
    
    return nextDate.toISOString().split('T')[0];
  };

  const handleFrequencyChange = (e) => {
    const frequency = e.target.value;
    setFormData(prev => ({
      ...prev,
      review_frequency: frequency,
      next_review_date: calculateNextReviewDate(frequency)
    }));
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="nextsteps-loading">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nextsteps-feedback">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] flex items-center space-x-3">
          <FaClipboardList />
          <span>Next Steps & Feedback</span>
        </h2>
        <p className="text-gray-400 mt-1">Review schedule, client feedback, and signatures</p>
      </div>

      {/* Section 1: Scheduled Review Frequency */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaCalendarAlt className="text-[#FFD700]" />
          <span>1. Scheduled Review Frequency</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Review Frequency */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Review Frequency</label>
            <div className="space-y-2">
              {['Quarterly', 'Half-Yearly', 'Yearly'].map((option) => (
                <label key={option} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="review_frequency"
                    value={option}
                    checked={formData.review_frequency === option}
                    onChange={handleFrequencyChange}
                    className="w-5 h-5 text-[#FFD700] border-[#2D5A8F] focus:ring-[#FFD700] bg-[#0A1929]"
                    data-testid={`frequency-${option.toLowerCase()}`}
                  />
                  <span className={`text-lg ${formData.review_frequency === option ? 'text-[#FFD700] font-semibold' : 'text-gray-300'}`}>
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Next Review Date */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Next Review Date</label>
            <input
              type="date"
              name="next_review_date"
              value={formData.next_review_date}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white text-lg focus:border-[#FFD700] focus:outline-none"
              data-testid="next-review-date"
            />
            {formData.next_review_date && (
              <p className="text-sm text-gray-500 mt-2">
                Scheduled: {new Date(formData.next_review_date).toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Client Feedback */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaComment className="text-[#FFD700]" />
          <span>2. Client Feedback</span>
        </h3>
        
        <div>
          <label className="block text-gray-400 text-sm mb-2 font-medium">Comments / Suggestions</label>
          <textarea
            name="client_feedback"
            value={formData.client_feedback}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none resize-none"
            placeholder="Enter client's feedback, comments, or suggestions about the financial plan..."
            data-testid="client-feedback"
          />
        </div>
      </div>

      {/* Section 3: Referrals / References */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaUserFriends className="text-[#FFD700]" />
          <span>3. Referrals / References</span>
        </h3>
        <p className="text-gray-500 text-sm mb-4">Please provide details of people who may benefit from financial planning</p>
        
        <div className="space-y-4">
          {[1, 2, 3].map((num) => (
            <div key={num} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[#0A1929] rounded-lg border border-[#2D5A8F]">
              <div>
                <label className="block text-gray-400 text-sm mb-1 font-medium">Referral {num} - Name</label>
                <input
                  type="text"
                  name={`referral_name_${num}`}
                  value={formData[`referral_name_${num}`]}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  placeholder="Full Name"
                  data-testid={`referral-name-${num}`}
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1 font-medium">Phone Number</label>
                <input
                  type="tel"
                  name={`referral_phone_${num}`}
                  value={formData[`referral_phone_${num}`]}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid={`referral-phone-${num}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4: Signatures */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-4 flex items-center space-x-2">
          <FaSignature className="text-[#FFD700]" />
          <span>4. Signatures</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Client Signature */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Client Signature</label>
            <div className="border-2 border-dashed border-[#2D5A8F] rounded-lg p-2 bg-[#0A1929]">
              <canvas
                ref={clientCanvasRef}
                width={350}
                height={150}
                className="w-full bg-[#0A1929] rounded cursor-crosshair touch-none"
                onMouseDown={(e) => startDrawing(e, true)}
                onMouseMove={(e) => draw(e, true)}
                onMouseUp={() => stopDrawing(true)}
                onMouseLeave={() => stopDrawing(true)}
                onTouchStart={(e) => startDrawing(e, true)}
                onTouchMove={(e) => draw(e, true)}
                onTouchEnd={() => stopDrawing(true)}
                data-testid="client-signature-canvas"
              />
            </div>
            <div className="flex space-x-2 mt-2">
              <button
                onClick={() => clearSignature(true)}
                className="px-4 py-2 text-sm bg-[#1E3A5F] text-gray-300 rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
              >
                Clear
              </button>
              <button
                onClick={() => confirmSignature(true)}
                className="px-4 py-2 text-sm bg-[#34D399] text-white rounded-lg hover:bg-[#2DD4BF] transition-all flex items-center space-x-1"
              >
                <FaCheck size={12} />
                <span>Confirm</span>
              </button>
            </div>
            {formData.client_signed_date && (
              <p className="text-sm text-[#34D399] mt-2">
                Signed on: {new Date(formData.client_signed_date).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>

          {/* Advisor Signature */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Advisor Signature</label>
            <div className="border-2 border-dashed border-[#2D5A8F] rounded-lg p-2 bg-[#0A1929]">
              <canvas
                ref={advisorCanvasRef}
                width={350}
                height={150}
                className="w-full bg-[#0A1929] rounded cursor-crosshair touch-none"
                onMouseDown={(e) => startDrawing(e, false)}
                onMouseMove={(e) => draw(e, false)}
                onMouseUp={() => stopDrawing(false)}
                onMouseLeave={() => stopDrawing(false)}
                onTouchStart={(e) => startDrawing(e, false)}
                onTouchMove={(e) => draw(e, false)}
                onTouchEnd={() => stopDrawing(false)}
                data-testid="advisor-signature-canvas"
              />
            </div>
            <div className="flex space-x-2 mt-2">
              <button
                onClick={() => clearSignature(false)}
                className="px-4 py-2 text-sm bg-[#1E3A5F] text-gray-300 rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
              >
                Clear
              </button>
              <button
                onClick={() => confirmSignature(false)}
                className="px-4 py-2 text-sm bg-[#34D399] text-white rounded-lg hover:bg-[#2DD4BF] transition-all flex items-center space-x-1"
              >
                <FaCheck size={12} />
                <span>Confirm</span>
              </button>
            </div>
            {formData.advisor_signed_date && (
              <p className="text-sm text-[#34D399] mt-2">
                Signed on: {new Date(formData.advisor_signed_date).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={saveData}
        disabled={saving}
        className={`w-full py-4 font-bold rounded-lg transition-all flex items-center justify-center space-x-2 ${
          saved 
            ? 'bg-[#34D399] text-white' 
            : 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] hover:from-[#FFD700] hover:to-[#D4AF37]'
        } disabled:opacity-50`}
        data-testid="save-nextsteps-btn"
      >
        {saving ? (
          <>
            <FaSpinner className="animate-spin" />
            <span>Saving...</span>
          </>
        ) : saved ? (
          <>
            <FaCheck />
            <span>Saved Successfully!</span>
          </>
        ) : (
          <>
            <FaSave />
            <span>Save Next Steps & Feedback</span>
          </>
        )}
      </button>

      {/* Disclaimer Note */}
      <div className="bg-[#0A1929] rounded-lg p-4 text-gray-500 text-sm border border-[#2D5A8F]">
        <p className="font-medium mb-1 text-gray-400">Note:</p>
        <p>By signing above, both the client and advisor acknowledge that they have reviewed the financial plan and agree to the next steps outlined in this document. The signatures serve as a record of the consultation.</p>
      </div>
    </div>
  );
};

export default NextStepsFeedback;
